import { observer } from 'mobx-react';
import React, { useCallback, useMemo } from 'react';

import { Facet, Sorting } from '@elastic/react-search-ui';
import { FacetContainerContext, MultiCheckboxFacet, SingleSelectFacet } from '@elastic/react-search-ui-views';
import type { SearchContextState } from '@elastic/search-ui';

import useStyles from './styles';

import './eui-light.css';

enum Fields {
    SCORE = 'score',
    EPSS = 'epss',
    AI_SCORE = 'aiScore',
    BULLETIN_FAMILY = 'bulletinFamily',
    TYPE = 'type',
    WILD_EXPLOITED = 'wildExploited',
    PUBLISHED = 'published',
    MODIFIED = 'modified'
}

const ElasticFilter = () => {
    const { classes } = useStyles();

    const sortOptions = useMemo(
        () => [
            { name: 'By date', value: 'published', direction: 'desc' },
            { name: 'By score', value: 'cvss3.cvssV3.baseScore', direction: 'desc' },
            { name: 'By views', value: 'viewCount', direction: 'desc' }
        ],
        []
    );

    const getMapContextToProps = useCallback(
        (field: Fields) =>
            (ctx: SearchContextState): FacetContainerContext => {
                const fieldFacet = ctx.facets[field];
                const fieldFilter = ctx.filters?.find((filter) => filter.field === field);

                if (!fieldFacet || !fieldFilter) {
                    return ctx;
                }

                const isSelected = (value) => Number(fieldFilter.values.includes(value));

                return {
                    ...ctx,
                    facets: {
                        ...ctx.facets,
                        [field]: fieldFacet.map(({ data, ...rest }) => ({
                            ...rest,
                            data: data.slice().sort(({ value: a }, { value: b }) => isSelected(b) - isSelected(a))
                        }))
                    }
                };
            },
        []
    );

    const mapContextToPropsByField = useMemo(
        () =>
            Object.values(Fields).reduce(
                (acc, field) => ({ ...acc, [field]: getMapContextToProps(field) }),
                {} as Record<Fields, ReturnType<typeof getMapContextToProps>>
            ),
        [getMapContextToProps]
    );

    return (
        <div className={classes.root}>
            <Sorting label="Sort by" className={classes.sorting} sortOptions={sortOptions} />
            <Facet
                className={classes.facet}
                field={Fields.PUBLISHED}
                label={'Published'}
                filterType="any"
                view={SingleSelectFacet}
                mapContextToProps={mapContextToPropsByField[Fields.PUBLISHED]}
            />
            <Facet
                className={classes.facet}
                field={Fields.SCORE}
                label={'CVSS 3 Score'}
                filterType="any"
                view={MultiCheckboxFacet}
                mapContextToProps={mapContextToPropsByField[Fields.SCORE]}
            />
            <Facet
                className={classes.facet}
                field={Fields.EPSS}
                label={'EPSS Score'}
                filterType="any"
                view={MultiCheckboxFacet}
                mapContextToProps={mapContextToPropsByField[Fields.EPSS]}
            />
            <Facet
                className={classes.facet}
                field={Fields.AI_SCORE}
                label={'AI Score'}
                filterType="any"
                view={MultiCheckboxFacet}
                mapContextToProps={mapContextToPropsByField[Fields.AI_SCORE]}
            />
            <Facet
                className={classes.facet}
                field={Fields.BULLETIN_FAMILY}
                label={'Bulletin Family'}
                filterType="any"
                mapContextToProps={mapContextToPropsByField[Fields.BULLETIN_FAMILY]}
            />
            <Facet
                className={classes.facet}
                field={Fields.TYPE}
                label={'Bulletin Type'}
                filterType="any"
                isFilterable={true}
                view={MultiCheckboxFacet}
                mapContextToProps={mapContextToPropsByField[Fields.TYPE]}
            />
            <Facet
                className={classes.facet}
                field={Fields.WILD_EXPLOITED}
                label={'is Wild Exploited'}
                filterType="any"
                mapContextToProps={mapContextToPropsByField[Fields.WILD_EXPLOITED]}
            />
            <Facet
                className={classes.facet}
                field={Fields.MODIFIED}
                label={'Modified'}
                filterType="any"
                view={SingleSelectFacet}
                mapContextToProps={mapContextToPropsByField[Fields.MODIFIED]}
            />
        </div>
    );
};

export default observer(ElasticFilter);
